import { Routes } from "./routes";

export const OPERATOR_ROLE = {
  OPERATOR: "operator",
  SUPER_ADMIN: "superAdmin",
  OWNER_ADMIN: "ownerAdmin",
  INDEXING: "indexing",
  TAGBOARD: "tagboard",
} as const;

export type OperatorRole = typeof OPERATOR_ROLE[keyof typeof OPERATOR_ROLE];

export const OPERATOR_COGNITO_GROUPS = {
  OPERATORS: "operators",
  SUPER_ADMINISTRATORS: "superAdministrators",
  OWNER_ADMINISTRATORS: "ownerAdministrators",
  INDEXING_OPERATORS: "indexingOperators",
  TAGBOARD_OPERATORS: "tagboardOperators",
} as const;

export function normalizeOperatorRole(raw: unknown): OperatorRole {
  const value = String(raw || "").trim();
  if (value === OPERATOR_ROLE.OWNER_ADMIN) return OPERATOR_ROLE.OWNER_ADMIN;
  if (value === OPERATOR_ROLE.SUPER_ADMIN) return OPERATOR_ROLE.SUPER_ADMIN;
  if (value === OPERATOR_ROLE.INDEXING) return OPERATOR_ROLE.INDEXING;
  if (value === OPERATOR_ROLE.TAGBOARD) return OPERATOR_ROLE.TAGBOARD;
  return OPERATOR_ROLE.OPERATOR;
}

export function normalizeOperatorRoles(
  rolesRaw: unknown,
  legacyRole?: unknown,
): OperatorRole[] {
  const fromArray = (Array.isArray(rolesRaw) ? rolesRaw : [])
    .map((r) => normalizeOperatorRole(r));
  const unique = fromArray.filter((role, index) => fromArray.indexOf(role) === index);
  if (unique.length > 0) return unique;
  return [normalizeOperatorRole(legacyRole)];
}

export function primaryRoleFromRoles(roles: OperatorRole[]): OperatorRole {
  if (roles.includes(OPERATOR_ROLE.OWNER_ADMIN)) return OPERATOR_ROLE.OWNER_ADMIN;
  if (roles.includes(OPERATOR_ROLE.SUPER_ADMIN)) return OPERATOR_ROLE.SUPER_ADMIN;
  if (roles.includes(OPERATOR_ROLE.OPERATOR)) return OPERATOR_ROLE.OPERATOR;
  if (roles.includes(OPERATOR_ROLE.INDEXING)) return OPERATOR_ROLE.INDEXING;
  if (roles.includes(OPERATOR_ROLE.TAGBOARD)) return OPERATOR_ROLE.TAGBOARD;
  return OPERATOR_ROLE.OPERATOR;
}

export function hasGeneralOperatorAccess(roles: OperatorRole[]): boolean {
  return roles.includes(OPERATOR_ROLE.OPERATOR)
    || roles.includes(OPERATOR_ROLE.SUPER_ADMIN)
    || roles.includes(OPERATOR_ROLE.OWNER_ADMIN);
}

export function isIndexingOnlyRoles(roles: OperatorRole[]): boolean {
  return roles.includes(OPERATOR_ROLE.INDEXING) && !hasGeneralOperatorAccess(roles);
}

export function isTagboardOnlyRoles(roles: OperatorRole[]): boolean {
  return roles.includes(OPERATOR_ROLE.TAGBOARD)
    && !hasGeneralOperatorAccess(roles)
    && !roles.includes(OPERATOR_ROLE.INDEXING);
}

export function hasSuperAdminAccess(role: string | undefined | null): boolean {
  const normalized = normalizeOperatorRole(role);
  return normalized === OPERATOR_ROLE.SUPER_ADMIN
    || normalized === OPERATOR_ROLE.OWNER_ADMIN;
}

export function isOwnerAdminRole(role: string | undefined | null): boolean {
  return normalizeOperatorRole(role) === OPERATOR_ROLE.OWNER_ADMIN;
}

export function isIndexingOperatorRole(role: string | undefined | null): boolean {
  return normalizeOperatorRole(role) === OPERATOR_ROLE.INDEXING;
}

export function isTagboardOperatorRole(role: string | undefined | null): boolean {
  return normalizeOperatorRole(role) === OPERATOR_ROLE.TAGBOARD;
}

export function roleDisplayName(role: string | undefined | null): string {
  switch (normalizeOperatorRole(role)) {
    case OPERATOR_ROLE.OWNER_ADMIN:
      return "Owner Admin";
    case OPERATOR_ROLE.SUPER_ADMIN:
      return "Super Admin";
    case OPERATOR_ROLE.INDEXING:
      return "Indexing Operator";
    case OPERATOR_ROLE.TAGBOARD:
      return "Tagboard";
    default:
      return "Operator";
  }
}

export function rolesDisplayName(roles: OperatorRole[]): string {
  if (!roles.length) return roleDisplayName(OPERATOR_ROLE.OPERATOR);
  return roles.map((r) => roleDisplayName(r)).join(", ");
}

function groupMatches(groups: string[] | undefined, target: string): boolean {
  if (!Array.isArray(groups)) return false;
  const needle = target.toLowerCase();
  return groups.some((g) => String(g).toLowerCase() === needle);
}

export function applyOperatorRoleFlagsFromGroups(groups: string[]): {
  isOwnerAdmin: boolean;
  isSuperAdmin: boolean;
  isIndexingOperator: boolean;
  isTagboardOperator: boolean;
} {
  const isOwnerAdmin = groupMatches(groups, OPERATOR_COGNITO_GROUPS.OWNER_ADMINISTRATORS);
  const isSuperAdminGroup = groupMatches(groups, OPERATOR_COGNITO_GROUPS.SUPER_ADMINISTRATORS);
  const isIndexingOperator = groupMatches(groups, OPERATOR_COGNITO_GROUPS.INDEXING_OPERATORS);
  const isTagboardOperator = groupMatches(groups, OPERATOR_COGNITO_GROUPS.TAGBOARD_OPERATORS);
  return {
    isOwnerAdmin,
    isSuperAdmin: isSuperAdminGroup || isOwnerAdmin,
    isIndexingOperator,
    isTagboardOperator,
  };
}

export function applyOperatorRoleFlagsFromMongoRoles(
  rolesRaw: unknown,
  legacyRole?: string | null,
): {
  isOwnerAdmin: boolean;
  isSuperAdmin: boolean;
  isIndexingOperator: boolean;
  isTagboardOperator: boolean;
  hasGeneralOperatorAccess: boolean;
  isIndexingOnly: boolean;
  isTagboardOnly: boolean;
  roles: OperatorRole[];
} {
  const roles = normalizeOperatorRoles(rolesRaw, legacyRole);
  return {
    isOwnerAdmin: roles.includes(OPERATOR_ROLE.OWNER_ADMIN),
    isSuperAdmin: roles.includes(OPERATOR_ROLE.SUPER_ADMIN)
      || roles.includes(OPERATOR_ROLE.OWNER_ADMIN),
    isIndexingOperator: roles.includes(OPERATOR_ROLE.INDEXING)
      || roles.includes(OPERATOR_ROLE.SUPER_ADMIN)
      || roles.includes(OPERATOR_ROLE.OWNER_ADMIN),
    isTagboardOperator: roles.includes(OPERATOR_ROLE.TAGBOARD),
    hasGeneralOperatorAccess: hasGeneralOperatorAccess(roles),
    isIndexingOnly: isIndexingOnlyRoles(roles),
    isTagboardOnly: isTagboardOnlyRoles(roles),
    roles,
  };
}

/** @deprecated Prefer applyOperatorRoleFlagsFromMongoRoles */
export function applyOperatorRoleFlagsFromMongoRole(role: string | undefined | null): {
  isOwnerAdmin: boolean;
  isSuperAdmin: boolean;
  isIndexingOperator: boolean;
  isTagboardOperator: boolean;
} {
  const flags = applyOperatorRoleFlagsFromMongoRoles(undefined, role);
  return {
    isOwnerAdmin: flags.isOwnerAdmin,
    isSuperAdmin: flags.isSuperAdmin,
    isIndexingOperator: flags.isIndexingOperator,
    isTagboardOperator: flags.isTagboardOperator,
  };
}

export function defaultLandingRouteFromGroups(groups: string[]): string {
  if (groupMatches(groups, OPERATOR_COGNITO_GROUPS.INDEXING_OPERATORS)
    && !groupMatches(groups, OPERATOR_COGNITO_GROUPS.OPERATORS)
    && !groupMatches(groups, OPERATOR_COGNITO_GROUPS.SUPER_ADMINISTRATORS)
    && !groupMatches(groups, OPERATOR_COGNITO_GROUPS.OWNER_ADMINISTRATORS)) {
    return Routes.INDEXING;
  }
  if (groupMatches(groups, OPERATOR_COGNITO_GROUPS.TAGBOARD_OPERATORS)) {
    return Routes.DOWNLOADS;
  }
  return Routes.GAMES;
}

export const INDEXING_OPERATOR_ROUTES: string[] = [
  Routes.STADIUM,
  Routes.STADIUM_SEATS,
  Routes.INDEXING,
  Routes.MAPPING,
  Routes.ACCOUNT,
  Routes.NOTIFICATIONS,
];

/** Tagboard users may only use Videoboard (Downloads library) + account/notifications. */
export const TAGBOARD_OPERATOR_ROUTES: string[] = [
  Routes.DOWNLOADS,
  Routes.ACCOUNT,
  Routes.NOTIFICATIONS,
];
